import { useState } from "react";
import { X } from "lucide-react";
import Loader from "../../utils/Loader";
import { toast } from "react-toastify";
import { forgotPassword, resetPassword } from "../../api";
import {
  selectSite,
  selectSiteLoginColor,
} from "../../utils/helper/commonSelectors";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const ForgotPasswordModal = () => {
  const siteDetails = useSelector(selectSite());
  const navigator = useNavigate();
  const [mobile, setMobile] = useState("");
  const [domain] = useState(siteDetails?.domainName?._id);
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1); // 1: Mobile input, 2: OTP & Password reset
  const LoginBtnColor = useSelector(selectSiteLoginColor());

  const handleMobileSubmit = async (e) => {
    e?.preventDefault();
    setError("");

    if (!mobile.trim()) {
      setError("Mobile number is required");
      return;
    }

    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(mobile)) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    setLoading(true);
    try {
      const response = await forgotPassword({ mobile, domain });
      if (response.success) {
        setStep(2);
        toast.success("OTP has been sent to your mobile");
      } else {
        setError(response.data.message || "Failed to send OTP");
        toast.error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setError("Something went wrong. Please try again.");
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e?.preventDefault();
    setError("");

    if (!otp.trim()) {
      setError("OTP is required");
      return;
    }
    if (!newPassword) {
      setError("New password is required");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const response = await resetPassword({
        mobile,
        otp,
        newPassword,
        domain,
      });
      if (response.success) {
        toast.success("Password reset successfully");
        navigator("/");
      } else {
        setError(response.data.message || "Failed to reset password");
        toast.error(response.data.message || "Failed to reset password");
      }
    } catch (error) {
      console.error("Reset password error:", error);
      setError("Something went wrong. Please try again.");
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    if (name === "mobile") {
      setMobile(value.replace(/\D/g, "").slice(0, 10));
    } else if (name === "otp") {
      setOtp(value.replace(/\D/g, "").slice(0, 6)); // Assuming 6-digit OTP
    } else if (name === "newPassword") {
      setNewPassword(value);
    } else if (name === "confirmPassword") {
      setConfirmPassword(value);
    }
    setError("");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#464850] text-white rounded-lg p-8 w-full max-w-md relative">
        <button
          onClick={() => {
            navigator("/");
          }}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          <X size={24} />
        </button>

        <h2 className="text-2xl font-bold mb-4 text-center">
          {step === 1 ? "Forgot Password" : "Reset Password"}
        </h2>

        {step === 1 ? (
          <>
            <p className="text-center text-[14px]  mb-6 text-gray-300">
              Enter your mobile number and we'll send you an OTP to reset your
              password
            </p>
            <form onSubmit={handleMobileSubmit}>
              <div className="mb-4">
                <label htmlFor="mobile" className="block text-[14px] mb-2">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  className={`w-full p-2 rounded bg-footerbg2 text-white placeholder-white ${
                    error ? "border-error" : ""
                  }`}
                  placeholder="Enter your mobile number"
                  value={mobile}
                  onChange={handleInputChange}
                  disabled={loading}
                  maxLength={10}
                />
                {error && <p className="text-error text-xs mt-1">{error}</p>}
              </div>

              <button
                type="submit"
                className="w-full bg-primary text-white p-2 rounded flex justify-center items-center space-x-3"
                disabled={loading}
                style={{
                  background: `linear-gradient(180deg, ${LoginBtnColor?.start}, ${LoginBtnColor?.end})`,
                }}
              >
                {loading ? (
                  <>
                    <Loader />
                    <span>Sending...</span>
                  </>
                ) : (
                  <span>Send Reset OTP</span>
                )}
              </button>
            </form>
          </>
        ) : (
          <>
            <p className="text-center mb-6 text-[14px] text-gray-300">
              Enter the OTP sent to {mobile} and your new password
            </p>
            <form onSubmit={handleResetSubmit}>
              <div className="mb-4">
                <label htmlFor="otp" className="block text-[14px] mb-2">
                  OTP
                </label>
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  className={`w-full p-2 rounded bg-footerbg2 text-white placeholder-white ${
                    error ? "border-error" : ""
                  }`}
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={handleInputChange}
                  disabled={loading}
                  maxLength={6}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="newPassword" className="block text-[14px] mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  className={`w-full p-2 rounded bg-footerbg2 text-white placeholder-white ${
                    error ? "border-error" : ""
                  }`}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="confirmPassword" className="block text-[14px] mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  className={`w-full p-2 rounded bg-footerbg2 text-white placeholder-white ${
                    error ? "border-error" : ""
                  }`}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {error && <p className="text-error text-xs mt-1">{error}</p>}
              </div>

              <button
                type="submit"
                className="w-full bg-primary text-white p-2 rounded flex justify-center items-center space-x-3"
                disabled={loading}
                style={{
                  background: `linear-gradient(180deg, ${LoginBtnColor?.start}, ${LoginBtnColor?.end})`,
                }}
              >
                {loading ? (
                  <>
                    <Loader />
                    <span>Resetting...</span>
                  </>
                ) : (
                  <span>Reset Password</span>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
